/** Right road section of the LUDOKA game board - PC skin (Red/right keyboard) */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { RIGHT_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for right section (Red)
 * Ordered right to left for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'RF1',
  'RC1',
  'RS1',
  'RF2',
  'RC2',
  'RS2',
  'RF3',
  'RC3',
  'RS3',
  'RF4',
  'RC4',
  'RS4',
  'RF5',
  'RC5',
  'RS5',
  'RF6',
  'RC6',
  'RS6',
  'RFINAL'
];

const SCALE_MULTIPLIER = 1.5; // Horizontal section needs larger pawns

export default function PcRightRoadSection({
  viewBox = '0 0 616 268',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  flippingPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Right road section with red final stretch"
    >
      {/* Dark ground border - base layer */}
      <path
        d="M587.69,228.96v25.58c-.38,4.46-1.7,10.47-6.37,14.29-6.61,5.4-16.26,3.41-23.29,2.39-18.56-2.7-11.41,2.49-54.6,2.26-27.78-.15-27.21-2.27-52.54-2.33-22.87-.05-24.04,1.68-48.14,2.31-29.19.77-30.59-1.69-72.46-2.52-30.19-.6-29.02.67-62.11,0-30.29-.61-28.8-1.61-57.9-2.02-12.89-.18-16.82-.04-59.59,0-13.83.01-16.43,0-23.88,0l-.12-27.39,461.01-12.56Z"
        fill="url(#pcrt_paint0)"
      />

      {/* Gray ground overlay - top layer */}
      <path
        d="M587.69,216.54v25.58c-.38,4.46-1.7,10.47-6.38,14.29-6.61,5.4-16.26,3.41-23.29,2.38-2.3-.34-4.21-.55-5.86-.66-11.7-.83-10.9,3.12-48.73,2.92-1.04,0-2.05,0-3.02-.03-4-.04-7.36-.14-10.29-.25-13.44-.54-17.79-1.69-32.71-1.98-1.97-.04-4.13-.06-6.52-.07-3.74,0-6.91.03-9.69.11-14.19.38-18.31,1.67-38.45,2.21-28.45.75-30.51-1.57-69.36-2.46-1.01-.03-2.04-.05-3.11-.07-2-.04-3.87-.08-5.62-.1-4.38-.06-8.01-.08-11.2-.06-2.2,0-4.18.02-6.05.05-10.64.15-17.65.54-39.24.11-25.49-.51-28.48-1.3-46.12-1.78-3.32-.09-7.17-.16-11.79-.23-.32,0-.63,0-.94,0-8.84-.13-13.77-.08-31.81-.03-3.26,0-6.96,0-11.18.02-4.57,0-9.74,0-15.68.02-13.83,0-16.5,0-23.94,0v-27.39l5.51-.17,37.75-1.03,66.56-1.81,56.47-1.54,64.66-1.76,39.8-1.08,190.21-5.18Z"
        fill="url(#pcrt_paint1)"
      />

      {/* Top right corner - dark gray placeholder (no stroke) */}
      <path
        d="M520.87,165.41h66.84v76.84h-76.84v-66.84c0-5.52,4.48-10,10-10Z"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Column 6: Blue accent center */}
      <rect
        x="434.03"
        y="165.41"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Middle center column - light gray */}
      <rect x="510.87" y="88.57" width="76.84" height="76.84" fill="#efefef" />

      {/* Column 6: Red gradient center */}
      <rect
        x="434.03"
        y="88.57"
        width="76.84"
        height="76.84"
        fill="url(#pcrt_paint2)"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Column 6: White center path square (center row) */}
      <rect x="440.36" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />
      {/* Column 6: White top path square */}
      <rect x="440.36" y="171.75" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

      {/* Column 5: Blue accent center */}
      <rect
        x="357.19"
        y="88.57"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Column 5: White center path square */}
      <rect x="363.52" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

      {/* Column 4: Red gradient center */}
      <rect
        x="280.35"
        y="88.57"
        width="76.84"
        height="76.84"
        fill="url(#pcrt_paint3)"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Column 4: White center path square */}
      <rect x="286.68" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

      {/* Column 3: Blue accent center */}
      <rect
        x="203.5"
        y="88.57"
        width="76.84"
        height="76.84"
        fill="#2468ff"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Column 3: White center path square */}
      <rect x="209.84" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

      {/* Column 2: Red gradient center */}
      <rect
        x="126.66"
        y="88.57"
        width="76.84"
        height="76.84"
        fill="url(#pcrt_paint4)"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Column 1: White center path square */}
      <rect x="133" y="94.91" width="64.17" height="64.17" rx="8.06" ry="8.06" fill="white" />

      {/* Top row squares - alternating gray */}
      <rect
        x="280.35"
        y="11.73"
        width="76.84"
        height="76.84"
        fill="#efefef"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect
        x="126.66"
        y="11.73"
        width="76.84"
        height="76.84"
        fill="#efefef"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect
        x="357.19"
        y="11.73"
        width="76.84"
        height="76.84"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect
        x="203.5"
        y="11.73"
        width="76.84"
        height="76.84"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Bottom right corner */}
      <path
        d="M520.87,165.41h66.84v76.84h-76.84v-66.84c0-5.52,4.48-10,10-10Z"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Top row column 6 */}
      <rect
        x="434.03"
        y="11.73"
        width="76.84"
        height="76.84"
        fill="#efefef"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Top right corner - gray with stroke */}
      <path
        d="M510.87,11.73h76.84v76.84h-66.84c-5.52,0-10-4.48-10-10V11.73h0Z"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Middle right column - gray */}
      <rect
        x="510.87"
        y="88.57"
        width="76.84"
        height="76.84"
        fill="#efefef"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Bottom row squares */}
      <rect
        x="280.35"
        y="165.41"
        width="76.84"
        height="76.84"
        fill="#efefef"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect
        x="126.66"
        y="165.41"
        width="76.84"
        height="76.84"
        fill="#efefef"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect
        x="357.19"
        y="165.41"
        width="76.84"
        height="76.84"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />
      <rect
        x="203.5"
        y="165.41"
        width="76.84"
        height="76.84"
        fill="#c4c4c4"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Star decoration - shadow layer */}
      <path
        d="M404.14,39.02l-6.62-11.95c-.64-1.15-2.28-1.2-2.98-.08l-7.23,11.59-13.41,2.6c-1.3.25-1.84,1.8-1,2.81l8.79,10.46-1.67,13.56c-.16,1.31,1.14,2.31,2.37,1.82l12.66-5.13,12.38,5.78c1.2.56,2.55-.37,2.46-1.69l-.97-13.63,9.32-9.99c.9-.97.43-2.54-.85-2.86l-13.26-3.29Z"
        fill="#303030"
      />
      {/* Star decoration - white top layer */}
      <path
        d="M404.14,37.72l-6.62-11.95c-.64-1.15-2.28-1.2-2.98-.08l-7.23,11.59-13.41,2.6c-1.3.25-1.84,1.8-1,2.81l8.79,10.46-1.67,13.56c-.16,1.31,1.14,2.31,2.37,1.82l12.66-5.13,12.38,5.78c1.2.56,2.55-.37,2.46-1.69l-.97-13.63,9.32-9.99c.9-.97.43-2.54-.85-2.86l-13.26-3.29Z"
        fill="white"
      />

      {/* Keyboard key labels */}
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(189.54 80.84)"
      >
        U
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(570.78 157.83) scale(1.33 1)"
      >
        &apos;&quot;
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(344.48 80.84)"
      >
        O
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(421.08 80.84)"
      >
        P
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(498.16 80.84)"
      >
        {`[{`}
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(574.63 80.84)"
      >
        {`]}`}
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(189.54 237.2)"
      >
        N
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(344.48 237.32)"
      >
        ,&lt;
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(267.44 237.32)"
      >
        M
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(267.44 80.84)"
      >
        I
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(421.08 237.32)"
      >
        .&gt;
      </text>
      <text
        fill="#717272"
        fontFamily="Minecraft, Minecraft"
        fontSize="11.61"
        transform="translate(570.78 237.32)"
      >
        /?
      </text>

      {/* Red gradient triangle/arrow on left */}
      <polygon
        points="11.4 126.99 126.66 11.73 125.93 242.25 11.4 126.99"
        fill="url(#pcrt_paint5)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Render pawns column by column from right to left for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        SCALE_MULTIPLIER,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents,
        flippingPawnIds
      )}

      <defs>
        {/* paint0: Dark gradient for ground border */}
        <linearGradient
          id="pcrt_paint0"
          x1="357.16"
          y1="257.3"
          x2="357.16"
          y2="283.98"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1a1a1a" />
          <stop offset="1" stopColor="#000000" />
        </linearGradient>

        {/* paint1: Gray gradient for ground overlay */}
        <linearGradient
          id="pcrt_paint1"
          x1="11.44"
          y1="238.86"
          x2="587.69"
          y2="238.86"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#666666" />
          <stop offset="1" stopColor="#333333" />
        </linearGradient>

        {/* paint2: Red gradient for Column 6 center */}
        <linearGradient
          id="pcrt_paint2"
          x1="434.03"
          y1="126.99"
          x2="510.87"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ff556a" />
          <stop offset="0.27" stopColor="#fb5267" />
          <stop offset="0.49" stopColor="#f24c5d" />
          <stop offset="0.68" stopColor="#e1414e" />
          <stop offset="0.86" stopColor="#ca3238" />
          <stop offset="1" stopColor="#b22222" />
        </linearGradient>

        {/* paint3: Red gradient for Column 4 center */}
        <linearGradient
          id="pcrt_paint3"
          x1="280.35"
          y1="126.99"
          x2="357.19"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ff556a" />
          <stop offset="0.27" stopColor="#fb5267" />
          <stop offset="0.49" stopColor="#f24c5d" />
          <stop offset="0.68" stopColor="#e1414e" />
          <stop offset="0.86" stopColor="#ca3238" />
          <stop offset="1" stopColor="#b22222" />
        </linearGradient>

        {/* paint4: Red gradient for Column 2 center */}
        <linearGradient
          id="pcrt_paint4"
          x1="126.66"
          y1="126.99"
          x2="203.5"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ff556a" />
          <stop offset="0.27" stopColor="#fb5267" />
          <stop offset="0.49" stopColor="#f24c5d" />
          <stop offset="0.68" stopColor="#e1414e" />
          <stop offset="0.86" stopColor="#ca3238" />
          <stop offset="1" stopColor="#b22222" />
        </linearGradient>

        {/* paint5: Red gradient for left triangle/arrow */}
        <linearGradient
          id="pcrt_paint5"
          x1="11.4"
          y1="126.99"
          x2="126.66"
          y2="126.99"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ff556a" />
          <stop offset="0.27" stopColor="#fb5267" />
          <stop offset="0.49" stopColor="#f24c5d" />
          <stop offset="0.68" stopColor="#e1414e" />
          <stop offset="0.86" stopColor="#ca3238" />
          <stop offset="1" stopColor="#b22222" />
        </linearGradient>
      </defs>
    </svg>
  );
}
